import isNil from 'lodash/isNil'

import LanguageHelper, { Language } from 'common/helpers/language'
import DateHelper from 'common/helpers/date'
import ProviderHelper from 'common/helpers/provider'
import { SaveTransmissionPayload } from 'modules/transmission/service'
import { FormData } from 'modules/transmission/components/transmission-form'
import {
  Visibility,
  CreatedFrom,
  ScheduleOption,
  EnableVodPlayback,
  Status,
  Provider,
  Transmission,
  PlaybackStatus
} from 'modules/transmission/types'
import { VIMEO_REGEX } from 'modules/player/constants'

export default class TransmissionHelper {
  static shouldShowExtensionTrigger = () => {
    return LanguageHelper.currentLanguage === Language.PT
      || LanguageHelper.currentLanguage === Language.ES
  }

  static getFormattedDate = (
    schedule: ScheduleOption,
    hour?: number,
    minute?: number,
    date?: Date
  ) => {
    let selectedDate = new Date()

    if (schedule === ScheduleOption.LATER && date && !isNil(hour)) {
      selectedDate = date
      selectedDate.setHours(hour, minute, 0)
    }

    const formattedHour = DateHelper.format(selectedDate, 'HH:mm')
    const formattedDate = DateHelper.format(selectedDate, 'YYYY-MM-DD')

    return { formattedHour, formattedDate }
  }

  static getTransmissionPayload = ({
    fields,
    visibility,
    status,
    createdFrom
  }: { fields: FormData, visibility: Visibility, status?: Status, createdFrom?: CreatedFrom }) => {
    const {
      code,
      date,
      description,
      enableVodPlayback,
      hour,
      minute,
      name,
      provider: providerOption,
      schedule,
      thumbnail,
      url,
      youtubePrivacy,
      zoneId
    } = fields
    const {
      formattedHour,
      formattedDate
    } = TransmissionHelper.getFormattedDate(schedule, hour, minute, date)

    const provider = ProviderHelper.mapOptionToProvider(providerOption)
    const hasThumbnail = !ProviderHelper.isExternal(providerOption) && fields.thumbnail

    const parameters: SaveTransmissionPayload = {
      ...(code ? { code } : {}),
      ...(status ? { status } : {}),
      ...(youtubePrivacy ? { youtubePrivacy } : {}),
      visibility,
      name,
      zoneId,
      description,
      createdFrom: createdFrom || CreatedFrom.WEBINAR,
      enableVodPlayback: enableVodPlayback === EnableVodPlayback.YES,
      liveStartAt: DateHelper.formatDateTimeToUtc(`${formattedDate} ${formattedHour}`, zoneId),
      ...(hasThumbnail ? { ...thumbnail } : {}),
      ...(provider
        ? { provider }
        : { url, provider: TransmissionHelper.discoverExternalProvider(url || '') }
      )
    }

    return parameters
  }

  static isHotmartProvider = (provider: Provider) => [
    Provider.HOTMART,
    Provider.MUX_STUDIO_HOTMART
  ].includes(provider)

  static discoverExternalProvider = (url: string): Provider.YOUTUBE | Provider.VIMEO => {
    if (VIMEO_REGEX.test(url)) {
      return Provider.VIMEO
    }

    return Provider.YOUTUBE
  }

  static isPlaybackAvailable = (
    { status, enableVodPlayback, playbackStatus }: Transmission,
    isHotmartProvider: boolean
  ) => {
    return status === Status.COMPLETED && (
      (!isHotmartProvider && enableVodPlayback)
      || (isHotmartProvider && enableVodPlayback && playbackStatus === PlaybackStatus.DOWNLOAD_AVAILABLE)
    )
  }

  static isPlaybackActive = ({ status, playbackStatus }: Transmission, isHotmartProvider: boolean) => {
    return status === Status.STARTED && (
      !isHotmartProvider || (isHotmartProvider && playbackStatus === PlaybackStatus.ACTIVE)
    )
  }
}
